/**
 * ViewSettings bridge — keeps the legacy Backbone TL.Model.ViewSettings model
 * and the Svelte view-settings store in sync.
 *
 * - Bidirectional: Backbone change → store, store change → Backbone.
 * - Backbone remains the persistence layer (its existing `change` listener in
 *   data-manager.js writes to indexedDB). The bridge never writes to storage
 *   directly; it relies on `model.set()` re-firing that listener.
 * - Two boolean flags prevent feedback loops between the directions.
 */

import {
  viewSettingsStore as defaultStore,
  type ViewSettingsStore,
} from '../stores/view-settings.svelte';
import type { ViewSettings } from '../types/view-settings';

/**
 * The subset of the Backbone model API the bridge depends on. Kept narrow so
 * the bridge can be unit-tested with a tiny mock.
 */
export interface BackboneViewSettingsModel {
  toJSON(): unknown;
  set(attrs: Record<string, unknown>, options?: Record<string, unknown>): unknown;
  on(event: string, callback: BackboneChangeListener): unknown;
  off(event: string, callback?: BackboneChangeListener): unknown;
}

export type BackboneChangeListener = (
  model: BackboneViewSettingsModel,
  value?: unknown,
  options?: Record<string, unknown>
) => void;

export interface ViewSettingsBridge {
  init(): void;
  destroy(): void;
  readonly active: boolean;
}

/**
 * Create a bridge instance. The `store` argument is intended for tests;
 * production callers should use `initViewSettingsBridge` which targets the
 * shared singleton.
 */
export function createViewSettingsBridge(
  model: BackboneViewSettingsModel,
  store: ViewSettingsStore = defaultStore
): ViewSettingsBridge {
  let active = false;
  let syncingFromBackbone = false;
  let syncingFromStore = false;
  let unsubscribeStore: (() => void) | null = null;

  const onBackboneChange: BackboneChangeListener = () => {
    if (syncingFromStore) return;
    syncingFromBackbone = true;
    try {
      const snapshot = structuredClone(model.toJSON()) as ViewSettings;
      store.reset(snapshot, /* silent */ true);
    } finally {
      syncingFromBackbone = false;
    }
  };

  const onStoreChange = (snapshot: ViewSettings): void => {
    if (syncingFromBackbone) return;
    syncingFromStore = true;
    try {
      model.set(structuredClone(snapshot) as Record<string, unknown>, {
        fromStore: true,
      });
    } finally {
      syncingFromStore = false;
    }
  };

  const bridge: ViewSettingsBridge = {
    get active() {
      return active;
    },
    init() {
      if (active) {
        console.warn('[viewSettingsBridge] init() called on an active bridge — ignoring');
        return;
      }
      // Step 1: seed the store with the model's current state, silently
      syncingFromBackbone = true;
      try {
        const initial = structuredClone(model.toJSON()) as ViewSettings;
        store.reset(initial, /* silent */ true);
      } finally {
        syncingFromBackbone = false;
      }
      // Step 2: attach listeners only after the initial sync is complete
      model.on('change', onBackboneChange);
      unsubscribeStore = store.onChange(onStoreChange);
      active = true;
    },
    destroy() {
      if (!active) return;
      model.off('change', onBackboneChange);
      unsubscribeStore?.();
      unsubscribeStore = null;
      syncingFromBackbone = false;
      syncingFromStore = false;
      active = false;
    },
  };

  return bridge;
}

/**
 * Module-level "active bridge" used by the legacy app. Calling this twice
 * destroys the previous bridge first — safe across SPA navigations where the
 * legacy view tears down without explicitly calling `destroy()`.
 */
let activeBridge: ViewSettingsBridge | null = null;

export function initViewSettingsBridge(
  model: BackboneViewSettingsModel
): ViewSettingsBridge {
  if (activeBridge) {
    console.warn(
      '[viewSettingsBridge] An active bridge already exists — destroying it before re-init'
    );
    activeBridge.destroy();
    activeBridge = null;
  }
  const bridge = createViewSettingsBridge(model);
  bridge.init();
  activeBridge = bridge;
  return bridge;
}

/** Returns the currently active bridge, if any. */
export function getActiveViewSettingsBridge(): ViewSettingsBridge | null {
  return activeBridge;
}

/** Test-only: clear module-level state. Not exported from the public index. */
export function _resetActiveViewSettingsBridge(): void {
  if (activeBridge) {
    activeBridge.destroy();
    activeBridge = null;
  }
}
